// A lean, prop-driven line-overlay chart for the Alt Surface Builder panels.
//
// It reuses the app's charting stack rather than adding one: lightweight-charts
// via useChartSetup().initMainChart (the same theme-aware init the dashboard
// timeseries widgets use). Those widgets are coupled to their own providers, so
// this is a presentational wrapper — it takes plain series props, owns no data,
// and recomputes nothing. The parent feeds it fresh series on every parameter
// or theme change.
import { Box } from '@mui/material';
import { IChartApi, ISeriesApi, LineData, Time } from 'lightweight-charts';
import { useEffect, useMemo, useRef } from 'react';

import { useChartSetup } from '../../widgets/immersive/chart/hooks/use-chart-setup';
import { useThemeMode } from '../../providers/theme-provider';

export interface AltChartPoint {
  // UTC calendar day (YYYY-MM-DD) or expiry key. lightweight-charts reads a
  // date string as a date-only, zone-free point — so the axis is UTC by
  // construction, matching the daily-close and expiry grids.
  time: string;
  value: number;
}

export interface AltChartSeries {
  id: string;
  label: string;
  // A theme-resolved hex colour (getTokenValue(...)) supplied by the parent, so
  // there is no colour literal here and the parent re-resolves it on theme flip.
  color: string;
  data: AltChartPoint[];
}

interface AltSurfaceChartProps {
  series: AltChartSeries[];
  height?: number;
}

interface SeriesHandle {
  id: string;
  ref: ISeriesApi<'Line'>;
}

// Drops the warm-up / gap NaNs and sorts ascending by time — lightweight-charts
// requires strictly increasing, finite-valued points.
const toLineData = (data: AltChartPoint[]): LineData[] =>
  data
    .filter((p) => Number.isFinite(p.value))
    .map((p) => ({ time: p.time as Time, value: p.value }))
    .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));

const percentFormatter = (price: number): string => `${(price * 100).toFixed(1)}%`;

const AltSurfaceChart = ({ series, height = 240 }: AltSurfaceChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | undefined>(undefined);
  const handlesRef = useRef<SeriesHandle[]>([]);
  const resizeObserverRef = useRef<ResizeObserver | undefined>(undefined);

  const { initMainChart, updateChartColors } = useChartSetup();
  const { resolvedMode } = useThemeMode();

  // Init once. The chart lives for the component's lifetime; series are diffed
  // in and out below rather than torn down on every render. initMainChart is a
  // fresh identity each render (the hook is not memoised), so the guard makes
  // re-runs no-ops — teardown is a separate mount-only effect, below, so a
  // re-render never destroys and rebuilds the chart.
  useEffect(() => {
    if (chartRef.current || !containerRef.current) return;
    initMainChart({
      containerRef,
      chartRef,
      actions: {},
    });
    chartRef.current?.applyOptions({
      rightPriceScale: { visible: true },
      leftPriceScale: { visible: false },
    });
    // autoSize resizes the canvas on a container resize but does NOT re-fit the
    // content, so if the container reaches its final width AFTER the data is set
    // (a layout-vs-data timing race whose order differs by environment/backend
    // speed) the bars keep the bar-spacing computed for the narrower width and
    // cluster against the right edge — blank left, data squashed right. Re-fit on
    // every container resize so the series always spans the full width.
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(() => {
        chartRef.current?.timeScale().fitContent();
      });
      resizeObserverRef.current.observe(containerRef.current);
    }
  }, [initMainChart]);

  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = undefined;
      chartRef.current?.remove();
      chartRef.current = undefined;
      handlesRef.current = [];
    },
    [],
  );

  // Re-apply theme-aware axis/grid colours when the mode flips. The line
  // colours ride in on the series prop, which the parent re-resolves per mode.
  useEffect(() => {
    updateChartColors(chartRef.current, resolvedMode);
  }, [resolvedMode, updateChartColors]);

  // Diff the series set against the live chart: add new lines, update existing
  // data/colour in place, remove any that have gone away. Runs on every series
  // identity change — i.e. live on each parameter edit.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const incoming = new Set(series.map((s) => s.id));
    handlesRef.current = handlesRef.current.filter((handle) => {
      if (incoming.has(handle.id)) return true;
      chart.removeSeries(handle.ref);
      return false;
    });

    series.forEach((s) => {
      let handle = handlesRef.current.find((h) => h.id === s.id);
      if (!handle) {
        const ref = chart.addLineSeries({
          color: s.color,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          priceFormat: { type: 'custom', formatter: percentFormatter },
        });
        handle = { id: s.id, ref };
        handlesRef.current.push(handle);
      } else {
        handle.ref.applyOptions({ color: s.color });
      }
      handle.ref.setData(toLineData(s.data));
    });

    chart.timeScale().fitContent();
  }, [series]);

  const legend = useMemo(
    () => series.map((s) => ({ id: s.id, label: s.label, color: s.color })),
    [series],
  );

  return (
    <div className="alt-surface-chart">
      <div className="alt-surface-chart__legend">
        {legend.map((item) => (
          <span key={item.id} className="alt-surface-chart__legend-item">
            <span
              className="alt-surface-chart__swatch"
              style={{ backgroundColor: item.color }}
              aria-hidden
            />
            {item.label}
          </span>
        ))}
      </div>
      <Box ref={containerRef} height={height} className="alt-surface-chart__canvas" />
    </div>
  );
};

export default AltSurfaceChart;
